/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef, useContext } from "react";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { MenuItem } from "primereact/menuitem";
import FeatherIcon from "feather-icons-react";
import { Link, useNavigate } from "react-router-dom";
import axios from "../../../../util/axiosconfig";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { Calendar } from "primereact/calendar";
import { ProgressSpinner } from "primereact/progressspinner";
import DataTableComponent from "../../../common/DataTable";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { Toast } from "primereact/toast";
import { useDebounce } from "../../../../util/debounce";
import { FilterContext } from "./FiltersContext";
import { Dialog } from "primereact/dialog";
import { showError } from "../../../../util/ShowStatus";
import type { Error } from "../../../../util/types";
import { ROLES } from "../../../../util/roleNameConstants";
import { CATHERINE_O_NEILL, STATUSES } from "../../../../util/constants";
import AddJobNumbers from "../AddInvoice/AddJobNumbers";
import { getErrorMessage } from "../../../../helper/getResponseMessage";
import { Currency } from "../AddInvoice/AddInvoice";
import { Tooltip } from "primereact/tooltip";
import { formatGBP } from "../../leak detection/helper/helperFunctions";
import { MultiSelect } from "primereact/multiselect";

export interface addInvoiceListing {
  invoiceID: number;
  billNo: string;
  totalAmount: number;
  fileType: string;
  originalFileName: string;
  fileNameS3: string;
  createdDate: string;
  poNumber: string;
  isActive: boolean;
  jobNumber: string;
  countryID: number;
  companyID?: number;
  countryName: string;
  currencySymbol: string;
  managerID?: null;
  createdBy?: number;
  commentByManager?: null;
  managerApprovalDate?: null;
  directorID?: null;
  commentByDirector?: null;
  directorApprovalDate?: null;
  accountantID?: null;
  commentByAccountant?: null;
  accountantApprovalDate?: null;
  commentBySuperAdmin: string;
  supplierID: number;
  supplierName: string;
  statusID: number;
  status: string;
  isEditable: boolean;
}
interface User {
  userID: any;
  userEmail: string;
  firstName: string;
  lastName: string;
  displayName: string;
  roleID: number;
  createdDate: Date;
  roleDisplayName: string;
  isActive: boolean;
  isEditable: boolean;
}

interface Status {
  statusID: string; // or number, depending on your API
  displayName: string;
}

interface Company {
  companyID: number;
  companyName: string;
  createdDate: Date;
  isActive: boolean;
}

const getDotClass = (status: {
  statusID: number;
  displayName: string;
}): string => {
  switch (status.displayName) {
    case STATUSES.NEW:
      return "dot bg-primary"; // Class for 'new' status
    case STATUSES.APPROVED_BY_MANAGER:
      return "dot bg-success"; // Class for 'success' status
    case STATUSES.REJECTED_BY_MANAGER:
      return "dot bg-error"; // Class for 'error' status
    case STATUSES.APPROVED_BY_SENIOR_MANAGER:
      return "dot bg-success"; // Class for 'success' status
    case STATUSES.REJECTED_BY_SENIOR_MANAGER:
      return "dot bg-error"; // Class for 'error' status
    case STATUSES.APPROVED_BY_DIRECTOR:
      return "dot bg-success"; // Class for 'success' status
    case STATUSES.REJECTED_BY_DIRECTOR:
      return "dot bg-error"; // Class for 'error' status
    case STATUSES.FINAL_APPROVAL_PENDING:
      return "dot tw-bg-yellow-600";
    default:
      return "dot bg-primary"; // Default class if no match is found
  }
};

const roleBodyTemplate = (rowData: any) => {
  const statusClass = getDotClass({
    statusID: rowData.statusID,
    displayName: rowData.status,
  });
  return (
    <span className="a-badge">
      <span className={statusClass}></span>
      <span className="badge-value tw-text-ellipsis tw-overflow-hidden tw-max-w-52">
        {/* {rowData.status === STATUSES.APPROVED_BY_SENIOR_MANAGER &&
        rowData.seniorManagerName === "Not Required"
          ? STATUSES.APPROVED_BY_MANAGER
          : rowData.status} */}
        {rowData.status}
      </span>
    </span>
  );
};

interface Filters {
  size: number;
  first?: number;
  search?: string | null;
  selectedStatuses?: string[]; // Optional property
  startDate?: string | null;
  endDate?: string | null;
  selectedManager?: any;
  selectedSeniorManager?: any;
  selectedDirector?: any;
  selectedCompany?: any;
  selectedInvoiceCreator?: any;
  selectedJobNo?: any;
  selectedManagerInput?: string | null;
  selectedSeniorManagerInput?: string | null;
  selectedDirectorInput?: string | null;
  selectedJobNoInput?: string | null;
  showSelectUserInput?: boolean;
}

function InvoiceManagement() {
  const navigate = useNavigate();
  const context = useContext(FilterContext);

  if (!context) {
    throw new Error("FilterPage must be used within a FilterProvider");
  }

  const { filters, setFilters, invoiceColumns, setInvoiceColumns } = context;

  const toast = useRef<Toast>(null);
  const menu = useRef(null as any);
  const columnMenu = useRef(null as any);
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const [allProjects, setAllProjects] = useState<any[]>([]);

  const [invoiceList, setInvoiceList] = useState<addInvoiceListing[]>([]);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState(0); // To hold the total number of records
  const [managers, setManagers] = useState<User[]>([]);
  const [seniorManagers, setSeniorManagers] = useState<User[]>([]);

  const [selectedManager, setSelectedManager] = useState<User | null>(null);
  const [selectedSeniorManager, setSelectedSeniorManager] =
    useState<User | null>(null);
  const [selectedDirector, setSelectedDirector] = useState<User | null>(null);

  const [jobEntries, setJobEntries] = useState([{ jobNumber: "", amount: "" }]);
  const [tempJobEntries, setTempJobEntries] = useState<any>([
    { jobNumber: "", amount: "" },
  ]);

  const [showAddJobNumber, setShowAddJobNumber] = useState<boolean>(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);

  const [invoiceCreators, setInvoiceCreators] = useState<User[]>([]);
  const [directors, setDirectors] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[] | null>(null);
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [invoiceListLoading, setInvoiceListLoading] = useState<boolean>(false);
  const [showExportDialog, setShowExportDialog] = useState<boolean>(false);
  const [resetPageNo, setResetPageNo] = useState<boolean>(false);
  const [tempPageNo, setTempPageNo] = useState<number>(0);

  const [currency, setCurrency] = useState<Currency[]>([
    {
      currencyID: 1,
      currencyName: "Euro",
      currencySymbol: "€",
      createdDate: "2024-09-23T11:00:26.000Z",
      isActive: true,
    },
    {
      currencyID: 2,
      currencyName: "Pound sterling",
      currencySymbol: "£",
      createdDate: "2024-09-23T11:00:26.000Z",
      isActive: true,
    },
  ]); // State to hold all currency

  const page = 0;

  const searchWithDebounce = useDebounce(filters?.search?.trim() ?? null);

  useEffect(() => {
    if (menu.current) {
      const myMenu = document.getElementById(menu.current.props.id);
      if (myMenu) {
        const computedStyle = window.getComputedStyle(myMenu);
        const topValue = parseInt(computedStyle.top, 10);
        if (topValue === 0) {
          myMenu.style.marginTop = "78px"; // Adjust this value as needed
        } else {
          myMenu.style.marginTop = "0";
        }
      }
    }
  }, [isMenuOpen]);

  const fetchAllProjects = async () => {
    try {
      const response = await axios.get(
        "tl-admin/project/all?page=-1&isActive=true",
      );
      const projectsWithTBC = response.data.result.sort((a: any, b: any) => {
        // Always put TBC first
        if (a.projectKey === "TBC") return -1;
        if (b.projectKey === "TBC") return 1;

        // Extract last 6 characters and convert to numbers
        const aKey = parseInt(a.projectKey.slice(-6), 10) || 0;
        const bKey = parseInt(b.projectKey.slice(-6), 10) || 0;

        // Compare numerically in DESCENDING order (bKey - aKey)
        return bKey - aKey;
      });
      setAllProjects(projectsWithTBC);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      showError(errorMessage, toast);
    }
  };

  useEffect(() => {
    fetchAllProjects();
  }, []);

  const SupplierNameTemplate = (rowData: addInvoiceListing) => {
    const handleSupplierClick = (jobId: string) => {
      navigate(`/layout/add-invoice?invoice=${jobId}`); // Navigate to the route with the job ID
    };
    return (
      <button
        onClick={(e) => {
          e.preventDefault();
          handleSupplierClick(rowData.invoiceID.toString());
        }}
        className="font-bold text-primary tw-text-nowrap bg-transparent border-none cursor-pointer"
      >
        {rowData.supplierName}
      </button>
    );
  };

  const uploadedOnBodyTemplate = (rowData: any) => {
    return (
      <span className="tw-text-nowrap">{formatDate(rowData.createdDate)}</span>
    );
  };
  const amountWithCurrencyTemplate = (rowData: any) => {
    const { totalAmount, currencySymbol } = rowData;
    return (
      <span className="tw-flex tw-text-ellipsis tw-overflow-hidden tw-max-w-52">
        {currencySymbol} {formatGBP(totalAmount)}
      </span>
    );
  };

  const getManagerComments = (data: any) => {
    let comment = "";
    try {
      comment = JSON.parse(data.commentByManager).comment;
    } catch (error) {
      comment = data.commentByManager;
    }
    return (
      <span className="tw-flex tw-text-ellipsis tw-overflow-hidden tw-max-w-52">
        {comment !== "" ? comment : "N/A"}
      </span>
    );
  };

  const getSeniorManagerComments = (data: any) => {
    let comment = "";
    try {
      comment = JSON.parse(data.commentBySeniorManager).comment;
    } catch (error) {
      comment = data.commentBySeniorManager;
    }
    return (
      <span className="tw-flex tw-text-ellipsis tw-overflow-hidden tw-max-w-52">
        {comment !== "" ? comment : "N/A"}
      </span>
    );
  };

  const fetchCurrencies = async () => {
    try {
      const response = await axios.get("user/currency");
      if (Array.isArray(response.data.result)) {
        setCurrency(response.data.result);
        // const defaultCurrency = response.data.result.find(
        //   (currency: { currencySymbol: string }) =>
        //     currency.currencySymbol === "£"
        // );
        // if (defaultCurrency && !invoiceParam) {
        //   handleInputChange("selectedCurrency", defaultCurrency); // Set Pound as default
        // }
      } else {
        console.error("API response is not an array");
      }
    } catch (error) {
      console.error("Error fetching currency:", error);
    }
  };

  const SeniorManagerNameTemplate = (rowData: any) => {
    return (
      <span className="font-bold tw-text-nowrap bg-transparent border-none cursor-pointer">
        {/* {rowData.status === STATUSES.APPROVED_BY_SENIOR_MANAGER &&
        rowData.seniorManagerName === "Not Required"
          ? rowData.managerName
          : rowData.seniorManagerName} */}
        {rowData.seniorManagerName}
      </span>
    );
  };

  // Enhance the columns with template functions
  const enhancedColumns = invoiceColumns.map((column) => {
    switch (column.field) {
      case "supplierName":
        return { ...column, body: SupplierNameTemplate };
      case "totalAmount":
        return { ...column, body: amountWithCurrencyTemplate };
      case "status":
        return { ...column, body: roleBodyTemplate };
      case "commentByManager":
        return { ...column, body: getManagerComments };
      case "seniorManagerName":
        return { ...column, body: SeniorManagerNameTemplate };
      case "commentBySeniorManager":
        return { ...column, body: getSeniorManagerComments };
      case "createdDate":
        return { ...column, body: uploadedOnBodyTemplate };
      case "invoiceGeneratedDate":
        return {
          ...column,
          body: (rowData: any) => {
            const date = new Date(rowData.invoiceGeneratedDate);
            const day = String(date.getDate()).padStart(2, "0");
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const year = date.getFullYear();
            return `${day}-${month}-${year}`;
          },
        };
      case "jobNumber":
        return {
          ...column,
          body: (rowData: any) => {
            return (
              <>
                <div
                  className="jobNumber-tooltip tw-truncate tw-max-w-xs tw-overflow-hidden tw-whitespace-nowrap tw-text-ellipsis"
                  data-pr-tooltip={(() => {
                    let fullList = [];
                    if (
                      rowData.jobNoMapping.length === 0 ||
                      rowData.jobNoMapping[0] === null
                    ) {
                      fullList = rowData.jobNumber;
                    } else {
                      fullList = rowData.jobNoMapping
                        .map(
                          (jobNumber: any) =>
                            `${jobNumber?.tlProjects?.projectKey ?? ""} - ${
                              formatGBP(jobNumber?.amount) ?? ""
                            }`,
                        )
                        .join(" || ");
                    }
                    return fullList;
                  })()}
                  data-pr-position="bottom"
                >
                  {(() => {
                    let fullList = [];
                    if (
                      rowData.jobNoMapping.length === 0 ||
                      rowData.jobNoMapping[0] === null
                    ) {
                      fullList = rowData.jobNumber;
                    } else {
                      fullList = rowData.jobNoMapping
                        .map(
                          (jobNumber: any) =>
                            `${jobNumber?.tlProjects?.projectKey ?? ""} - ${
                              formatGBP(jobNumber?.amount) ?? ""
                            }`,
                        )
                        .join(" || ");
                    }
                    const words = fullList?.split(/\s+/) ?? "";
                    const truncated =
                      words?.length > 50
                        ? words?.slice(0, 50).join(" ") + "..."
                        : fullList;

                    return truncated;
                  })()}
                </div>
                <Tooltip target=".jobNumber-tooltip" />
              </>
            );
          },
        };
      default:
        return column;
    }
  });

  const invoiceManagement = async (
    page: any,
    size: number,
    searchWithDebounce: any,
    type?: string,
  ) => {
    if (type === "export-invoice") {
      setIsExporting(true);
    } else setInvoiceListLoading(true); // Set loading to true when fetching data
    try {
      const params: any = {};
      // if (type === "export-invoice") {
      //   setShowExportDialog(true);
      // }
      // Ensure params is of the correct type (object) before setting properties
      if (typeof params === "object" && params !== null) {
        params.size = size; // Set the number of rows per page
        params.page = page; // Set the page index
        if (searchWithDebounce !== null && searchWithDebounce !== undefined) {
          params.search = searchWithDebounce;
        }
      }

      // Add filters to params only if they are selected
      if (filters?.selectedStatuses?.length) {
        params.status = filters?.selectedStatuses
          .map((status: any) => status.statusID)
          .join(","); // Join array into a string for the query
        // Ensure we send a stable, sortedlist of status IDs (ascending)
        // const sortedStatusIds = (filters.selectedStatuses as any[])
        //   .map((status: any) => Number(status.statusID))
        //   .filter((id) => !isNaN(id))
        //   .sort((a: number, b: number) => a - b)
        //   .join(",");
        // params.status = sortedStatusIds;
      } else {
        if (localStorage.roleName.includes(ROLES.SUPER_ADMIN)) {
          params.status = "1,2,3,4,5,8, 9, 10";
        } else if (localStorage.roleName.includes(ROLES.MANAGER)) {
          params.status = "1";
        } else if (localStorage.roleName.includes(ROLES.SENIOR_MANAGER)) {
          params.status = "2";
        } else if (localStorage.roleName.includes(ROLES.DIRECTOR)) {
          params.status = "9";
        } else if (localStorage.roleName.includes(ROLES.ACCOUNTANT)) {
          params.status = "4";
        }
      }

      if (filters.startDate && filters.endDate) {
        params.startDate = new Date(filters?.startDate)
          .toISOString()
          .split("T")[0]; // Convert date to YYYY-MM-DD
      }
      if (filters.endDate) {
        params.endDate = new Date(filters?.endDate).toISOString().split("T")[0];
      }
      if (filters.selectedManager && filters.selectedManager.length > 0) {
        params.managerID = filters.selectedManager
          .map((manager: any) => manager.userID)
          .join(","); // Join array into a string for the query
      }
      if (
        filters.selectedSeniorManager &&
        filters.selectedSeniorManager.length > 0
      ) {
        params.seniorManagerID = filters.selectedSeniorManager
          .map((seniorManager: any) => seniorManager.userID)
          .join(","); // Join array into a string for the query
      }
      if (filters.selectedDirector && filters.selectedDirector.length > 0) {
        params.directorID = filters?.selectedDirector
          .map((director: any) => director.userID)
          .join(",");
      }
      if (filters.selectedCompany) {
        params.companyID = filters?.selectedCompany;
      }
      if (filters.selectedInvoiceCreator) {
        params.createdBy = filters?.selectedInvoiceCreator.userID;
      }
      if (filters.selectedInvoiceCreator === undefined) {
        params.createdBy = 0;
      }
      if (filters.selectedAccountantUser) {
        params.createdBy = filters?.selectedAccountantUser.userID;
      }
      if (filters.selectedJobNo) {
        params.jobNumber = filters?.selectedJobNo.projectID;
      }

      if (type === `invoiceFilters`) {
        const response = await axios.get<any>(`/invoice/list`, { params });

        setInvoiceListLoading(false); // Stop loading
        setInvoiceList(response.data.result);
        setTotalRecords(response.data.totalRecords); // Assuming the API returns the total count
      } else if (type === "export-invoice") {
        await axios.get("user/export-invoice", { params });
      }
    } catch (err: unknown) {
      const error = err as any;
      showError(
        error?.response?.data?.Message ?? "",
        toast,
        error?.message ?? "",
      );
    } finally {
      setInvoiceListLoading(false); // Stop loading
      setIsExporting(false);
      if (type === "export-invoice") {
        setShowExportDialog(true);
      }
    }
  };

  useEffect(() => {
    if (
      (!filters?.startDate && !filters?.endDate) ||
      (filters?.startDate && filters?.endDate)
    ) {
      invoiceManagement(
        (filters?.first ?? 0) === 0
          ? 0
          : searchWithDebounce !== null && searchWithDebounce !== ""
          ? 0
          : resetPageNo === true
          ? 0
          : (filters?.first ?? 10) / (filters?.size ?? 1),
        // (filters?.first ?? 0) === 0 ? 0 : (filters?.first ?? 10) / (filters?.size ?? 1),
        // page,
        filters?.size,
        searchWithDebounce,
        "invoiceFilters",
      );
      if (resetPageNo === true) {
        setFilters((prevFilters: Filters) => ({
          ...prevFilters,
          first: 0,
        }));
      }
    }
  }, [
    page,
    filters?.size,
    filters?.selectedStatuses,
    filters?.endDate,
    filters?.selectedManager,
    filters?.selectedSeniorManager,
    filters?.selectedDirector,
    filters?.selectedCompany,
    filters?.selectedInvoiceCreator,
    filters?.selectedAccountantUser,
    filters?.selectedJobNo,
    searchWithDebounce,
  ]);

  const onPageChange = (event: PaginatorPageChangeEvent) => {
    setResetPageNo(false);
    setFilters((prevFilters: Filters) => ({
      ...prevFilters,
      first: event.first,
    }));
    setFilters((prevFilters: Filters) => ({
      ...prevFilters,
      size: event.rows,
    }));
    const currentPage = Math.floor(event.first / event.rows); // Calculate the current page index
    invoiceManagement(
      currentPage,
      event.rows,
      searchWithDebounce,
      "invoiceFilters",
    ); // Fetch users for the new page and size
  };
  const handleStatusClick = (status: Status) => {
    setResetPageNo(true);
    setFilters((prevFilters: any) => {
      // Ensure selectedStatuses is always an array
      const currentStatuses = prevFilters.selectedStatuses ?? [];

      // Determine the new statuses array
      const statusExists = currentStatuses.some(
        (s: any) => s.statusID === status.statusID,
      );

      // If the status exists and removing it would leave the array empty, do nothing
      if (statusExists && currentStatuses.length === 1) {
        return prevFilters; // Return previous filters without changes
      }

      const updatedStatuses = statusExists
        ? currentStatuses.filter((s: any) => s.statusID !== status.statusID) // Remove the status
        : [...currentStatuses, status]; // Add the status

      return {
        ...prevFilters,
        selectedStatuses: updatedStatuses,
      };
    });
  };

  const fetchStatuses = async () => {
    try {
      const response = await axios.get("/user/status");
      if (response.data.StatusCode === 200) {
        setStatuses(response.data.result);
      } else {
        console.error(response.data.Message);
      }
    } catch (err: unknown) {
      const error = err as any;
      showError(error?.response?.data.Message, toast, error?.message);
    }
  };

  const fetchManagers = async () => {
    try {
      const response = await axios.get("user/list?roleID=2&page=-1"); // API endpoint for directors
      setManagers(response.data.result); // Set directors data
    } catch (err: unknown) {
      const error = err as Error;
      showError(error?.response?.data.Message, toast);
    }
  };

  const fetchSeniorManagers = async () => {
    try {
      const response = await axios.get("user/list?roleID=15&page=-1"); // API endpoint for senior managers
      setSeniorManagers(response.data.result); // Set senior managers data
    } catch (err: unknown) {
      const error = err as Error;
      showError(error?.response?.data.Message, toast);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`user/companies`);
      setCompanies(response.data.result);
    } catch (err: unknown) {
      const error = err as Error;
      showError(error?.response?.data.Message, toast);
    }
  };

  const fetchInvoiceCreators = async () => {
    try {
      const response = await axios.get("user/role-filter?roleID=[1,4]");
      setInvoiceCreators(response.data.result);
    } catch (err: unknown) {
      const error = err as Error;
      showError(error?.response?.data.Message, toast);
    }
  };

  const fetchDirectors = async () => {
    try {
      const response = await axios.get("user/list?roleID=3&page=-1"); // API endpoint for directors

      if (Array.isArray(response.data.result)) {
        setDirectors(response.data.result); // Set directors data
      }
    } catch (err: unknown) {
      const error = err as Error;
      showError(error?.response?.data.Message, toast);
    }
  };

  useEffect(() => {
    if (
      localStorage.roleName.includes(ROLES.SUPER_ADMIN) ||
      localStorage.roleName.includes(ROLES.ACCOUNTANT)
    ) {
      setInvoiceColumns((prevColumns) => {
        // Check if "createdBy" column already exists
        const exists = prevColumns.some((col) => col.field === "createdBy");
        if (!exists) {
          return [
            ...prevColumns,
            {
              field: "createdBy",
              header: "Created By",
              visible: false,
            },
          ];
        }
        return prevColumns;
      });
    }
    fetchManagers();
    fetchSeniorManagers();
    fetchCurrencies();
    fetchStatuses();
    fetchDirectors();
    fetchCompanies();
    fetchInvoiceCreators();
  }, []);

  const clearFilters = () => {
    setResetPageNo(false);
    setSelectedManager(null);
    setSelectedSeniorManager(null);
    setSelectedDirector(null);
    setFilters((prevFilters: Filters) => {
      const roleNamesString = localStorage.getItem("roleName");
      const roleNames = roleNamesString
        ? roleNamesString.split(",").map((name) => name.trim())
        : [];

      let currentSelectedStatuses: any = [];

      if (roleNames.includes(ROLES.SUPER_ADMIN)) {
        currentSelectedStatuses.push(
          { statusID: 1, displayName: "New" },
          { statusID: 2, displayName: "Approved By Manager" },
          { statusID: 3, displayName: "Rejected By Manager" },
          { statusID: 9, displayName: "Approved By Senior Manager" },
          { statusID: 10, displayName: "Rejected By Senior Manager" },
          { statusID: 4, displayName: "Approved By Director" },
          { statusID: 5, displayName: "Rejected By Director" },
          { statusID: 8, displayName: "Final Approval Pending" },
        );
      }
      if (roleNames.includes(ROLES.MANAGER)) {
        currentSelectedStatuses.push({ statusID: 1, displayName: "New" });
      }
      if (roleNames.includes(ROLES.SENIOR_MANAGER)) {
        currentSelectedStatuses.push({
          statusID: 2,
          displayName: "Approved By Manager",
        });
      }
      if (roleNames.includes(ROLES.DIRECTOR)) {
        currentSelectedStatuses.push(
          {
            statusID: 2,
            displayName: "Approved By Manager",
          },
          { statusID: 9, displayName: "Approved By Senior Manager" },
        );
        if (localStorage.userEmail === CATHERINE_O_NEILL.userEmail) {
          currentSelectedStatuses.push({
            statusID: 8,
            displayName: "Final Approval Pending",
          });
        }
      }

      // Remove duplicate statuses if a user has multiple roles
      currentSelectedStatuses = currentSelectedStatuses.filter(
        (status: Status, index: number, self: any) =>
          index === self.findIndex((s: any) => s.statusID === status.statusID),
      );

      return {
        ...prevFilters,
        size: 10,
        page: 0,
        first: 0,
        selectedStatuses: currentSelectedStatuses,
        startDate: null,
        endDate: null,
        dates: [],
        selectedManager: null,
        selectedSeniorManager: null,
        selectedDirector: null,
        selectedCompany: null,
        selectedInvoiceCreator: null,
        selectedJobNo: null,
        selectedManagerInput: null,
        selectedSeniorManagerInput: null,
        selectedDirectorInput: null,
        selectedJobNoInput: null,
        showSelectUserInput: false,
      };
    });
  };
  const formatFilterDate = (date: Date | null): string | null => {
    if (!date) return null;
    return new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
    )
      .toISOString()
      .split("T")[0]; // Format as 'YYYY-MM-DD'
  };

  // const roleNames = localStorage.roleName
  //   .split(",")
  //   .map((name: string) => name.trim());
  // const isOnlyManager =
  //   roleNames.length === 1 && roleNames[0] === ROLES.MANAGER;

  const roleNamesString = localStorage.getItem("roleName") ?? "";
  const roleNames = roleNamesString
    .split(",")
    .map((name: string) => name.trim())
    .filter(Boolean);

  // Treat a single "Manager" or "Senior Manager" role as "only manager" if needed
  const isOnlyManager =
    roleNames.length === 1 &&
    (roleNames[0] === ROLES.MANAGER || roleNames[0] === ROLES.SENIOR_MANAGER);

  const InvoiceDateConst = (date: any): void => {
    const formattedStartDate = date[0] ? formatFilterDate(date[0]) : null;
    const formattedEndDate = date[1] ? formatFilterDate(date[1]) : null;

    setFilters((prevFilters: Filters) => ({
      ...prevFilters,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      dates: date,
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === " ") {
      e.stopPropagation(); // Prevent focus shift or parent events
    }

    if (e.key === "Enter") {
      invoiceManagement(
        page,
        filters?.size,
        searchWithDebounce,
        "invoiceFilters",
      );
    }
  };

  const jobNumberOptionsTemplate = (option: any) => {
    return (
      <div>
        {option.projectKey} - {option.name}
      </div>
    );
  };

  const jobNumberValueTemplate = (option: any) => {
    if (!option) {
      return "Select Job Number";
    }
    return (
      <div>
        {option.projectKey} - {option.name}
      </div>
    );
  };

  const items = [
    {
      label: "Status",
      items: statuses.map((status: { statusID: any; displayName: string }) => ({
        template: (
          <div>
            <span
              key={status.statusID} // Ensure each dynamically rendered element has a unique key
              className={`a-badge tw-cursor-pointer tw-mx-5 tw-mt-3 ${
                filters?.selectedStatuses?.some(
                  (s: any) => s.displayName === status.displayName,
                )
                  ? "selected-filter-status"
                  : ""
              }`}
              onClick={() => handleStatusClick(status)} // Pass the status object to the click handler
            >
              <span className={`${getDotClass(status)}`}></span>
              <span className="badge-value">{status.displayName}</span>
            </span>
          </div>
        ),
      })),
    },
    {
      template: (
        <div
          className="tw-flex tw-flex-col tw-gap-2 tw-mx-5 tw-w-62 tw-mt-3"
          onClick={(e) => e.stopPropagation()} // Prevents the menu from closing
        >
          <label htmlFor="InvoiceDate" className="tw-mt-3 tw-w-1/2">
            Invoice Date
          </label>
          <p className="tw-text-purple-600">Select start and end date</p>
          <Calendar
            className="tw-px-[14px] tw-py-[5px] tw-border tw-border-solid tw-border-gray-300 tw-rounded-md tw-h-11 placeholder:tw-font-normal"
            dateFormat="dd-mm-yy"
            selectionMode="range"
            placeholder="Select Invoice Date"
            value={filters?.dates}
            maxDate={new Date()}
            onChange={(e) => {
              e.stopPropagation();
              setResetPageNo(true);
              InvoiceDateConst(e.value);
            }}
            showIcon
            readOnlyInput
            hideOnRangeSelection
          />
        </div>
      ),
    },
    {
      template: (
        <>
          {isOnlyManager === true ? null : (
            <div className="tw-flex tw-flex-col tw-gap-2 tw-mx-5 tw-w-62 tw-mt-3">
              <label htmlFor="jobNoInput">Job No.</label>
              <div className="relative">
                <Dropdown
                  className={`tw-border tw-border-solid tw-border-gray-300 tw-rounded-md placeholder:tw-font-normal tw-w-full`}
                  value={filters?.selectedJobNo ?? ""}
                  // disabled={isEditMode === false}
                  options={allProjects}
                  itemTemplate={jobNumberOptionsTemplate}
                  valueTemplate={jobNumberValueTemplate}
                  onChange={(e) => {
                    setResetPageNo(true);
                    setFilters((prev: any) => ({
                      ...prev,
                      selectedJobNo: e.value,
                    }));
                  }}
                  placeholder="Select Job Number"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (
                      e.key === " " ||
                      e.key === "Enter" ||
                      e.key === "ArrowUp" ||
                      e.key === "ArrowDown"
                    ) {
                      e.stopPropagation();
                    }
                  }}
                  filterBy="projectKey,name"
                  filterMatchMode="contains"
                  filter
                />
              </div>
            </div>
          )}
        </>
      ),
    },
    {
      template: (
        <>
          {(localStorage.roleName.includes(ROLES.SUPER_ADMIN) ||
            localStorage.roleName.includes(ROLES.ACCOUNTANT)) && (
            <div>
              {filters?.showSelectUserInput ? (
                <div className="tw-flex tw-flex-col tw-gap-2 tw-mx-5 tw-w-62 tw-mt-3 tw-mb-4">
                  <label htmlFor="invoiceCreator">Created By</label>
                  <Dropdown
                    className="tw-h-11 tw-border tw-border-solid tw-border-gray-300 tw-rounded-md tw-w-full placeholder:tw-font-normal filter-dropdown"
                    value={
                      filters?.selectedInvoiceCreator?.displayName
                        ? filters?.selectedInvoiceCreator
                        : null
                    }
                    panelClassName="tw-ml tw-w-[20px]"
                    options={invoiceCreators}
                    onChange={(e) => {
                      setResetPageNo(true);
                      setFilters((prevFilters: Filters) => ({
                        ...prevFilters,
                        selectedInvoiceCreator: e.value,
                      }));
                    }}
                    optionLabel="displayName" // Assuming 'roles' contains an array of objects with a 'displayName' key
                    placeholder="Select User"
                    onClick={(e) => e.stopPropagation()}
                    showClear
                  />
                </div>
              ) : (
                <Button
                  label={`Created By Me`}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent the click from closing the modal
                    setResetPageNo(true);
                    setFilters((prevFilters: Filters) => ({
                      ...prevFilters,
                      showSelectUserInput: true,
                      selectedInvoiceCreator: {
                        ...prevFilters.selectedInvoiceCreator,
                        userID: 0,
                      },
                    }));
                  }}
                  className="tw-text-blue-500 tw-ml-5 tw-mt-4"
                  link
                />
              )}
            </div>
          )}
        </>
      ),
    },
    {
      template: (
        <>
          {isOnlyManager === true ? null : (
            <div className="tw-flex tw-flex-col tw-gap-2 tw-mx-5 tw-w-62 tw-mt-3">
              <label htmlFor="managerInput">Manager</label>
              <div className="relative">
                <MultiSelect
                  value={selectedManager || []}
                  options={managers || []}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    setResetPageNo(true);
                    setSelectedManager(e.value);
                    setFilters((prevFilters: Filters) => ({
                      ...prevFilters,
                      selectedManager: e.value,
                    }));
                  }}
                  showSelectAll
                  maxSelectedLabels={2}
                  selectAllLabel="Select All"
                  placeholder="Select a Manager"
                  optionLabel="displayName"
                  className={`tw-border tw-border-solid tw-border-gray-300 tw-rounded-md placeholder:tw-font-normal tw-w-full`}
                  filter
                />
              </div>
            </div>
          )}
        </>
      ),
    },
    {
      template: (
        <>
          {localStorage.roleName.includes(ROLES.SENIOR_MANAGER) &&
          localStorage.roleName.split(",").length === 1 ? null : (
            <div className="tw-flex tw-flex-col tw-gap-2 tw-mx-5 tw-w-62 tw-mt-3">
              <label htmlFor="seniorManagerInput">Senior Manager</label>
              <div className="relative">
                <MultiSelect
                  value={selectedSeniorManager || []}
                  options={seniorManagers || []}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    setResetPageNo(true);
                    setSelectedSeniorManager(e.value);
                    setFilters((prevFilters: Filters) => ({
                      ...prevFilters,
                      selectedSeniorManager: e.value,
                    }));
                  }}
                  showSelectAll
                  maxSelectedLabels={2}
                  selectAllLabel="Select All"
                  placeholder="Select a Senior Manager"
                  optionLabel="displayName"
                  className={`tw-border tw-border-solid tw-border-gray-300 tw-rounded-md placeholder:tw-font-normal tw-w-full`}
                  filter
                />
              </div>
            </div>
          )}
        </>
      ),
    },
    {
      template: (
        <>
          {localStorage.roleName.includes(ROLES.DIRECTOR) &&
          localStorage.roleName.split(",").length === 1 ? null : (
            <div className="tw-flex tw-flex-col tw-gap-2 tw-mx-5 tw-w-62 tw-mt-3 tw-mb-4">
              <label htmlFor="directorInput">Director</label>
              <div className="relative">
                <MultiSelect
                  value={selectedDirector || []}
                  options={directors || []}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    setResetPageNo(true);
                    setSelectedDirector(e.value);
                    setFilters((prevFilters: Filters) => ({
                      ...prevFilters,
                      selectedDirector: e.value,
                    }));
                  }}
                  showSelectAll
                  maxSelectedLabels={2}
                  selectAllLabel="Select All"
                  placeholder="Select a Director"
                  optionLabel="displayName"
                  className={`tw-border tw-border-solid tw-border-gray-300 tw-rounded-md placeholder:tw-font-normal tw-w-full`}
                  filter
                />
              </div>
            </div>
          )}
        </>
      ),
    },
    {
      template: (
        <div className="tw-flex tw-flex-col tw-gap-2 tw-mx-5 tw-w-62 tw-mt-3 tw-mb-4">
          <label htmlFor="UploadedOn">Company</label>
          <Dropdown
            className="tw-h-11 tw-border tw-border-solid tw-border-gray-300 tw-rounded-md tw-w-full placeholder:tw-font-normal filter-dropdown"
            value={filters?.selectedCompany ?? null}
            options={
              companies?.map((company) => ({
                label: company.companyName, // Displayed text
                value: company.companyID, // Underlying value
              })) || []
            } // Fallback to an empty array if companies is null
            onChange={(e) => {
              setResetPageNo(true);
              setFilters((prevFilters: Filters) => ({
                ...prevFilters,
                selectedCompany: e.value,
              }));
            }}
            optionLabel="label" // Assuming 'roles' contains an array of objects with a 'role' key
            placeholder="Select Company"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ),
    },
    {
      template: (
        <div className="tw-flex tw-flex-col tw-gap-2 tw-mx-5 tw-w-62 tw-mt-3 tw-mb-4">
          <Button
            className="tw-flex tw-text-center tw-px-3 tw-py-2 tw-rounded tw-bg-red-500 tw-font-medium tw-gap-2 tw-text-white"
            onClick={clearFilters}
          >
            Clear Filters
          </Button>
        </div>
      ),
    },
  ];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);

    // Extract and format the parts you need
    const day = date.getUTCDate().toString().padStart(2, "0"); // 25
    const month = date.toLocaleString("en-US", { month: "short" }); // Sep
    const year = date.getUTCFullYear(); // 2024

    // Format the time
    const time = date.toLocaleTimeString("en-GB"); // 09:59:04

    return `${day} ${month} ${year} ${time}`;
  };

  const handleCheckboxChange = (field: string) => {
    setInvoiceColumns((prevColumns: any) =>
      prevColumns.map((col: { field: string; visible: any }) =>
        col.field === field ? { ...col, visible: !col.visible } : col
      )
    );
  };

  const columnItems = [
    {
      template: (
        <div className="tw-flex tw-flex-col tw-gap-2 tw-mx-5 tw-w-62 tw-mt-3">
          <span className="tw-font-medium">Select Columns</span>
          {invoiceColumns.map((col, index) => {
            return (
              <div key={col.field} className="tw-flex tw-items-center tw-gap-2">
                <input
                  type="checkbox"
                  checked={col.visible}
                  onChange={() => handleCheckboxChange(col.field)}
                  className="tw-border-blue-500 tw-w-[17px] tw-cursor-pointer tw-h-[17px] tw-bg-white tw-checked:bg-blue-500 tw-checked:border-blue-500 tw-transition-colors tw-duration-200"
                  disabled={index < 6} // Disable the first seven checkboxes
                />
                <label
                  htmlFor={col.field}
                  className="tw-font-medium tw-text-gray-800"
                >
                  {col.header}
                </label>
              </div>
            );
          })}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="card ">
        <Toast ref={toast} />
        <Tooltip
          target=".jobNumber-tooltip"
          className="tw-whitespace-pre-line tw-text-center"
        />
        <Dialog
          visible={showExportDialog}
          onHide={() => setShowExportDialog(false)}
          header="✅ Successfull "
        >
          <div>
            Your export generation has started. You can view the progress in the
            'Export History' tab. To access it please {""}
            <Link
              to="/layout/export-history"
              className="tw-text-primary tw-underline hover:tw-text-[#152846] tw-font-medium"
              onClick={() => setShowExportDialog(false)}
            >
              click here.
            </Link>
          </div>
        </Dialog>

        <div className="tw-flex tw-justify-between tw-items-center tw-w-full tw-gap-4 tw-bg-[#F8FAFC] tw-px-4 tw-py-4">
          <FloatLabel>
            <InputText
              value={filters?.search ?? ""}
              id="searchInvoiceText"
              onChange={(e) => {
                setResetPageNo(false);
                setFilters((prevFilter: Filters) => ({
                  ...prevFilter,
                  search: e.target.value,
                }));
                if (e.target.value === "") {
                  setFilters((prevFilter: Filters) => ({
                    ...prevFilter,
                    first: tempPageNo,
                  }));
                } else {
                  if (filters?.first) {
                    setTempPageNo(filters?.first);
                  }
                  setFilters((prevFilter: Filters) => ({
                    ...prevFilter,
                    first: 0,
                  }));
                }
              }}
              onKeyDown={handleKeyDown} // Add the key down handler here
              className="tw-p-2 tw-border tw-border-[#B7B7B7] tw-rounded"
            />
            <label htmlFor="search">Search</label>
          </FloatLabel>
          <div className="tw-flex tw-justify-end tw-items-center tw-gap-4">
            <div>
              <Button
                className="tw-flex tw-px-3 tw-py-2 tw-font-medium tw-gap-2"
                severity="secondary"
                onClick={(event) => {
                  setIsMenuOpen(!isMenuOpen);
                  menu.current?.toggle(event);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="28"
                  height="28"
                  viewBox="0 0 28 28"
                  fill="none"
                >
                  <path
                    d="M25.6663 3.5H2.33301L11.6663 14.5367V22.1667L16.333 24.5V14.5367L25.6663 3.5Z"
                    stroke="#222222"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="tw-text-sm">Filters</span>
              </Button>
              <Menu
                id="myMenu"
                className={`tw-w-72`}
                model={items as MenuItem[]}
                popup
                ref={menu}
              />
            </div>
            {localStorage.roleName.includes(ROLES.SUPER_ADMIN) ||
            localStorage.roleName.includes(ROLES.ACCOUNTANT) ? (
              <>
                <Button
                  className="tw-flex tw-px-3 tw-py-2 tw-rounded tw-bg-[#555555] tw-border-[#555555] tw-text-white tw-font-medium"
                  label="Export Invoice"
                  icon="pi pi-upload"
                  onClick={() =>
                    invoiceManagement(
                      page,
                      filters?.size,
                      searchWithDebounce,
                      "export-invoice",
                    )
                  }
                  loading={isExporting}
                  disabled={invoiceList.length === 0 || isExporting}
                />
                {localStorage.roleName.includes(ROLES.SUPER_ADMIN) ||
                localStorage.roleName.includes(ROLES.ACCOUNTANT) ? (
                  <Button
                    className="tw-flex tw-px-3 tw-py-2 tw-rounded bg-primary border-primary tw-text-white tw-font-medium"
                    label="Add Invoice"
                    icon="pi pi-plus"
                    onClick={() => navigate("/layout/add-invoice")}
                  />
                ) : null}
              </>
            ) : null}
          </div>
        </div>
        <div className="card-body tw-relative tw-min-h-80 table-p-0">
          {invoiceListLoading ? (
            <div className="tw-h-full tw-w-full tw-bg-white tw-flex tw-justify-center tw-items-center tw-absolute tw-top-0 tw-left-0 tw-z-50">
              <ProgressSpinner />
            </div>
          ) : (
            <>
              <div className="tw-absolute tw-right-5 tw-top-2 tw-z-50">
                <Menu
                  className="tw-w-72"
                  model={columnItems as MenuItem[]}
                  popup
                  ref={columnMenu}
                />
                <Button
                  className="tw-flex tw-mt-2 tw-rounded tw-bg-white tw-font-medium tw-gap-2"
                  severity="secondary"
                  onClick={(event) => columnMenu.current?.toggle(event)}
                  tooltip="Add Columns"
                  tooltipOptions={{ position: "left" }}
                >
                  <FeatherIcon icon="plus-square" />
                </Button>
              </div>
              <DataTableComponent
                rowData={invoiceList}
                columns={enhancedColumns.filter((col) => col.visible)}
                isMarginRight={true}
              />
              <Paginator
                first={filters?.first}
                rows={filters?.size}
                totalRecords={totalRecords}
                rowsPerPageOptions={[5, 10, 25, 50]}
                onPageChange={onPageChange}
              />
            </>
          )}
        </div>
      </div>
      <AddJobNumbers
        showAddJobNumber={showAddJobNumber}
        title="View Job No"
        rowData={selectedRowData}
        isEditMode={false}
        invoiceParam={true}
        allProjects={allProjects}
        currency={currency}
        onClose={() => setShowAddJobNumber(false)}
        jobEntries={jobEntries}
        setJobEntries={setJobEntries}
        tempJobEntries={tempJobEntries}
        setTempJobEntries={setTempJobEntries}
      />
    </>
  );
}

export default InvoiceManagement;
